/* eslint-disable max-len */
// =============================================================================
// XUẤT "Bản kê khai thành tích cá nhân - Giải thưởng Nữ sinh KHCN Việt Nam" (.docx)
// Cách làm (giống kvc.exportProfile): nạp template tối giản (chỉ có 1 placeholder
// {~~content}), dựng TOÀN BỘ nội dung hồ sơ thành 1 chuỗi HTML rồi inject qua
// docxtemplater + html-module. Mọi JSON.parse đều bọc try/catch, dữ liệu
// thiếu/legacy không làm vỡ file.
// =============================================================================
import dayjs from "dayjs";
import PizZip from "pizzip";
import PizZipUtils from "pizzip/utils/index.js";
import Docxtemplater from "docxtemplater";

import { saveAs } from "file-saver";

// Template tối giản dùng chung (chỉ chứa placeholder {~~content}) — không phụ
// thuộc chương trình, nội dung được dựng hoàn toàn bằng HTML bên dưới.
import sample from "../assets/docs/KVC_Ho_so.docx";
import HTMLModule from "./docxtemplater-html-module";

import {
  FORM_CONSTANTS,
  REPORTS_CONSTANTS,
  RESEARCH_CONSTANTS,
  REWARD_CONSTANTS,
  MONOGRAPHS_CONSTANTS,
} from "../constants";
import { KEYS, APP_SCENE } from "../constants/enums";

const loadFile = (url, callback) => PizZipUtils.getBinaryContent(url, callback);

// --- Helpers an toàn -------------------------------------------------------
const parseJson = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "null");
    return parsed == null ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
};

const esc = (value) =>
  String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const findTitle = (options, value) =>
  (options || []).find((o) => o.value === value)?.title || "";

// Ô "minh chứng": link DOI/ngoài dùng nguyên; file upload thì ghép domain.
// Chấp nhận value là string hoặc mảng (lấy phần tử đầu). Trả về HTML <a> an toàn.
const previewCell = (domain, value) => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return "";
  const isExternal = /^https?:\/\//i.test(raw);
  const href = isExternal ? raw : `${domain || ""}${raw}`;
  return `<a href="${esc(href)}">Minh chứng</a>`;
};

// --- Dựng bảng -------------------------------------------------------------
const TABLE_STYLE =
  "width:100%;border-collapse:collapse;margin:4px 0 14px;font-size:13px;";
const TH =
  "border:1px solid #000;padding:4px 6px;background:#d9d9d9;text-align:left;font-weight:bold;";
const TD = "border:1px solid #000;padding:4px 6px;vertical-align:top;";

// rows: mảng các mảng cell (đã là HTML, KHÔNG tự escape ở đây).
const buildTable = (headers, rows) => {
  if (!rows || rows.length === 0) {
    return '<p style="margin:2px 0 12px;font-style:italic;">Không có.</p>';
  }
  const head = headers.map((h) => `<th style="${TH}">${h}</th>`).join("");
  const body = rows
    .map(
      (r) => `<tr>${r.map((c) => `<td style="${TD}">${c}</td>`).join("")}</tr>`
    )
    .join("");
  return `<table style="${TABLE_STYLE}"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
};

const sectionTitle = (text) =>
  `<p style="margin:16px 0 4px;font-size:15px;font-weight:bold;background:#c8c7cc;padding:4px 6px;">${esc(text)}</p>`;

const subTitle = (text) =>
  `<p style="margin:10px 0 2px;font-weight:bold;">${esc(text)}</p>`;

// Tiêu đề bảng dùng chung cho bài báo / báo cáo khoa học.
const PAPER_HEADERS = ["STT", "Nội dung", "Phân loại", "Số TG", "Vai trò", "Minh chứng"];

const exportProfile = (rawForm) => {
  const form = rawForm || {};
  const profile = form.profile || {};
  const domain = process.env.REACT_APP_DOMAIN;

  loadFile(sample, (error, content) => {
    if (error) throw error;

    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
      linebreaks: true,
      paragraphLoop: true,
      modules: [new HTMLModule({})],
    });

    // ================= I. THÔNG TIN CÁ NHÂN =================
    const bonusMap = {
      khong: "Không",
      "khuyet-tat": "Là nữ sinh viên khuyết tật",
    };
    const infoRows = [
      ["Họ và tên", profile.name],
      ["Ngày sinh", profile.birth ? dayjs(profile.birth).format("DD/MM/YYYY") : ""],
      ["Giới tính", findTitle(FORM_CONSTANTS.GENDER_OPTIONS, profile.gender)],
      ["Dân tộc", profile.ethnic],
      ["Số điện thoại", profile.phone],
      ["Email", profile.email],
      ["Website/Facebook", profile.website],
      ["Tên trường/học viện đang theo học", form.university],
      ["Tên Khoa/Viện", form.faculty],
      ["Điện thoại liên hệ Đoàn trường/học viện", form.facultyPhone],
      ["Ngành học", form.major],
      ["Nhóm ngành đăng ký", findTitle(FORM_CONSTANTS.FIELD_OPTIONS[APP_SCENE], form.field)],
      ["Sinh viên năm thứ", form.studentYear],
      ["Điểm TB chung học tập (thang điểm 4)", form.gpa],
      ["Đơn vị đề cử", form.nominator],
      ["Điện thoại đơn vị đề cử", form.nominatorPhone],
      ["Đoàn thể chính trị", findTitle(FORM_CONSTANTS.POLITICAL_UNION_OPTIONS, form.politicalUnion) || form.politicalUnion],
      ["Điểm ưu tiên", bonusMap[form.bonus] || ""],
      ["Ngoại ngữ", form.languague],
      ["Năng khiếu", form.gifted],
      ["Link video giới thiệu và 05 ảnh hoạt động", form.videoUrl],
    ];
    const infoTable = `<table style="${TABLE_STYLE}"><tbody>${infoRows
      .map(
        ([label, value]) =>
          `<tr><td style="${TD}width:40%;font-weight:bold;">${esc(label)}</td><td style="${TD}">${esc(value)}</td></tr>`
      )
      .join("")}</tbody></table>`;

    // ================= II. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ =================
    const solutionArr = Array.isArray(form.formSolution) ? form.formSolution : [];
    const researchArr = Array.isArray(form.formScientificResearch) ? form.formScientificResearch : [];
    const reportArr = Array.isArray(form.formScientificReport) ? form.formScientificReport : [];
    const rewardArr = Array.isArray(form.formReward) ? form.formReward : [];

    const paperTypeOptions = [
      ...(REPORTS_CONSTANTS.TYPE_OPTIONS.ALL || []),
      ...(RESEARCH_CONSTANTS.TYPE_OPTIONS.DOMESTIC || []),
      ...(RESEARCH_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL || []),
      ...(MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.DOMESTIC || []),
      ...(MONOGRAPHS_CONSTANTS.TYPE_OPTIONS.INTERNATIONAL || []),
    ];
    const rewardTypeOptions = [
      ...(REWARD_CONSTANTS.TYPE_OPTIONS[KEYS.HTQT] || []),
      ...(REWARD_CONSTANTS.TYPE_OPTIONS[KEYS.HTQG] || []),
      ...(REWARD_CONSTANTS.TYPE_OPTIONS[KEYS.HTCS] || []),
      ...(REWARD_CONSTANTS.TYPE_OPTIONS[KEYS.NCKHCB] || []),
      ...(REWARD_CONSTANTS.TYPE_OPTIONS[KEYS.NCKHCT] || []),
    ];

    const mc = (obj) => (obj?.link ? previewCell(null, obj.link) : previewCell(domain, obj?.preview));

    // Sáng chế / giải pháp hữu ích.
    const solutionRows = (types) =>
      solutionArr
        .map((item) => item?.solution || {})
        .filter((r) => types.includes(r.type))
        .map((r, i) => [
          `${i + 1}`,
          esc(r.title),
          esc(r.author),
          esc(r.issueAt ? dayjs(r.issueAt).format("DD/MM/YYYY") : ""),
          mc(r),
        ]);

    // Bài báo khoa học (tạp chí) — chia quốc tế / trong nước theo filterType.
    const paperRows = (list, extract) =>
      list.map((item, i) => {
        const r = extract(item) || {};
        return [
          `${i + 1}`,
          esc(r.title),
          esc(findTitle(paperTypeOptions, r.type)),
          esc(r.numberAuthor),
          esc(findTitle(RESEARCH_CONSTANTS.ROLE_OPTIONS, r.role)),
          mc(r),
        ];
      });

    const researchInternational = researchArr.filter(
      (item) => item?.scientificResearch?.filterType === KEYS.INTERNATIONAL
    );
    const researchDomestic = researchArr.filter(
      (item) => item?.scientificResearch?.filterType === KEYS.DOMESTIC
    );

    // Giải thưởng / đề tài (dữ liệu formReward) — chia nhóm theo type.
    const REWARD_GROUPS = {
      [KEYS.HTQT]: ["g1", "g2", "g3-1"],
      [KEYS.HTQG]: ["g3-2", "g4", "g5"],
      [KEYS.HTCS]: ["nstb-g1", "nstb-g2", "nstb-g3"],
      [KEYS.NCKHCB]: ["nstb-n1", "nstb-n2", "nstb-n3", "nstb-n4"],
      [KEYS.NCKHCT]: ["nstb-n5", "nstb-n6", "nstb-n7"],
    };
    const rewardOf = (group) =>
      rewardArr
        .map((item) => item?.reward || {})
        .filter((r) => REWARD_GROUPS[group].includes(r.type));

    // Bảng "giải thưởng thi đấu" (HT*): STT / Nội dung / Phân loại / Minh chứng.
    const rewardRows = (group) =>
      rewardOf(group).map((r, i) => [
        `${i + 1}`,
        esc(r.title),
        esc(findTitle(rewardTypeOptions, r.type)),
        mc(r),
      ]);
    // Bảng "đề tài NCKH" (NCKH*): thêm cột Vai trò.
    const projectRewardRows = (group) =>
      rewardOf(group).map((r, i) => [
        `${i + 1}`,
        esc(r.title),
        esc(findTitle(rewardTypeOptions, r.type)),
        esc(findTitle(FORM_CONSTANTS.ROLE_OPTIONS?.slice(0, 2), r.role)),
        mc(r),
      ]);

    // ================= III/IV/V. Các bảng lưu dạng JSON =================
    const majorArr = parseJson(form.majorAchievement, []);
    const rewardJsonArr = parseJson(form.reward, []);
    const socialArr = parseJson(form.socialActivity, []);

    const majorRows = (Array.isArray(majorArr) ? majorArr : []).map((r, i) => [
      `${i + 1}`,
      esc(r?.[`${KEYS.MAJOR_ACHIEVEMENT}Content`]),
      esc(findTitle(FORM_CONSTANTS.LEVEL_INPUT_OPTIONS, r?.[`${KEYS.MAJOR_ACHIEVEMENT}Type`])),
      previewCell(domain, r?.[`${KEYS.MAJOR_ACHIEVEMENT}PreviewFile`]),
    ]);
    const rewardJsonRows = (Array.isArray(rewardJsonArr) ? rewardJsonArr : []).map((r, i) => [
      `${i + 1}`,
      esc(r?.[`${KEYS.REWARD}Content`]),
      esc(findTitle(FORM_CONSTANTS.LEVEL_INPUT_OPTIONS, r?.[`${KEYS.REWARD}Type`])),
      previewCell(domain, r?.[`${KEYS.REWARD}PreviewFile`]),
    ]);
    const socialRows = (Array.isArray(socialArr) ? socialArr : []).map((r, i) => [
      `${i + 1}`,
      esc(r?.[`${KEYS.SOCIAL_ACTIVITY}Content`]),
      previewCell(domain, r?.[`${KEYS.SOCIAL_ACTIVITY}PreviewFile`]),
    ]);

    // ================= GHÉP TOÀN BỘ HTML =================
    const html = [
      `<p style="text-align:center;font-weight:bold;margin:0;">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>`,
      `<p style="text-align:center;font-weight:bold;margin:0;">Độc lập – Tự do – Hạnh phúc</p>`,
      `<p style="text-align:center;margin:0;">------</p>`,
      `<h2 style="text-align:center;">BẢN KÊ KHAI THÀNH TÍCH CÁ NHÂN THAM GIA GIẢI THƯỞNG NỮ SINH KHOA HỌC CÔNG NGHỆ VIỆT NAM</h2>`,

      sectionTitle("I. THÔNG TIN CÁ NHÂN"),
      infoTable,

      sectionTitle("II. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ"),
      subTitle("1. Bằng độc quyền sáng chế, giải pháp hữu ích thuộc các nhóm ngành xét chọn"),
      buildTable(["STT", "Tên công trình", "Tác giả", "Ngày cấp", "Minh chứng"], solutionRows(["sc", "scqg", "gp"])),
      subTitle("2. Sáng chế/giải pháp hữu ích đã được chấp nhận đơn hợp lệ thuộc các nhóm ngành xét chọn"),
      buildTable(["STT", "Tên công trình", "Tác giả", "Ngày cấp", "Minh chứng"], solutionRows(["ptns-sc", "ptns-scqg", "ptns-gp"])),
      subTitle("3. Bài báo khoa học đăng trên tạp chí quốc tế/quốc gia thuộc các ngành xét giải"),
      subTitle("3.1. Bài báo khoa học trên tạp chí uy tín quốc tế"),
      buildTable(PAPER_HEADERS, paperRows(researchInternational, (item) => item?.scientificResearch)),
      subTitle("3.2. Bài báo khoa học đăng trên tạp chí trong nước"),
      buildTable(PAPER_HEADERS, paperRows(researchDomestic, (item) => item?.scientificResearch)),
      subTitle("4. Bài báo khoa học đăng trên hội thảo quốc tế/quốc gia thuộc các ngành xét giải"),
      buildTable(PAPER_HEADERS, paperRows(reportArr, (item) => item?.scientificReport)),
      subTitle("5. Tham gia nghiên cứu khoa học thuộc các ngành xét giải"),
      subTitle("5.1. Đề tài nghiên cứu khoa học cấp Bộ đã nghiệm thu đạt"),
      buildTable(["STT", "Nội dung", "Phân loại", "Vai trò", "Minh chứng"], projectRewardRows(KEYS.NCKHCB)),
      subTitle("5.2. Đề tài nghiên cứu khoa học cấp Trường đã nghiệm thu đạt"),
      buildTable(["STT", "Nội dung", "Phân loại", "Vai trò", "Minh chứng"], projectRewardRows(KEYS.NCKHCT)),
      subTitle("6. Đạt Huy chương/Giải thưởng thuộc các ngành xét giải"),
      subTitle("6.1. Huy chương/Giải thưởng cấp quốc tế"),
      buildTable(["STT", "Nội dung", "Phân loại", "Minh chứng"], rewardRows(KEYS.HTQT)),
      subTitle("6.2. Huy chương/Giải thưởng cấp quốc gia"),
      buildTable(["STT", "Nội dung", "Phân loại", "Minh chứng"], rewardRows(KEYS.HTQG)),
      subTitle("6.3. Huy chương/Giải thưởng cấp tỉnh, thành, ngành"),
      buildTable(["STT", "Nội dung", "Phân loại", "Minh chứng"], rewardRows(KEYS.HTCS)),

      sectionTitle("III. THÀNH TÍCH TRONG LĨNH VỰC CHUYÊN NGÀNH"),
      buildTable(["STT", "Hình thức và nội dung thành tích", "Cấp thành tích", "Minh chứng"], majorRows),

      sectionTitle("IV. THÀNH TÍCH KHEN THƯỞNG"),
      buildTable(["STT", "Nội dung khen thưởng", "Cấp khen thưởng", "Minh chứng"], rewardJsonRows),

      sectionTitle("V. THÀNH TÍCH HOẠT ĐỘNG ĐOÀN, ĐỘI, HỘI"),
      buildTable(["STT", "Mô tả", "Minh chứng"], socialRows),

      `<p style="margin:16px 0 4px;">Tôi xin hoàn toàn chịu trách nhiệm trước pháp luật, Ban tổ chức Giải thưởng về tính chính xác và các thông tin đã khai trong hồ sơ đăng ký trực tuyến tham gia xét giải thưởng.</p>`,
      `<p style="text-align:right;font-style:italic;margin:8px 0;">…………, ngày……tháng……năm ${dayjs().format("YYYY")}</p>`,

      `<table style="width:100%;margin-top:24px;border:none;"><tbody><tr>` +
        `<td style="width:50%;text-align:center;border:none;"><b>XÁC NHẬN CỦA ĐƠN VỊ</b></td>` +
        `<td style="width:50%;text-align:center;border:none;"><b>NGƯỜI KHAI</b><br/><i>(Ký, ghi rõ họ tên)</i><br/><br/><br/><br/>${esc(profile.name)}</td>` +
        `</tr></tbody></table>`,
    ].join("");

    doc.render({ content: html });

    const blob = doc.getZip().generate({
      type: "blob",
      mimeType:
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    saveAs(blob, "Ban_ke_khai_GTNS.docx");
  });
};

export default exportProfile;
